import React from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Edit } from "lucide-react";
import useOpenDrawer from "@/hooks/useOpenDrawer";
import AdditionalComponentEditForm from "./AdditionalComponentEditForm";
import ModuleCompatibilityDrawer from "./ModuleCompatibilityDrawer";
import { summarizeConditions } from "./componentConditions";

interface AdditionalComponentItemProps {
  componentConnection: {
    advancedProductId: number;
    enabled: boolean;
    component_group_id: number;
    advanced_product_id: number;
    conditions?: any;
    additional_component: {
      id: number;
      code: string;
      component_sku?: string;
      conditions?: any;
      image?: {
        src_thumbnail: string;
        src_md?: string;
        src?: string;
      };
      additional_component_profiles: Array<{
        name: string;
        language: string;
      }>;
    };
  };
  language: string;
  advancedProductId: number;
  manufacturerId: number;
  componentGroup: any;
  moduleCompatibilityCount?: number;
  totalModulesCount?: number;
  linkedComponentsCount?: number;
}

export const AdditionalComponentItem: React.FC<
  AdditionalComponentItemProps
> = ({
  componentConnection,
  language,
  advancedProductId,
  manufacturerId,
  componentGroup,
  moduleCompatibilityCount,
  totalModulesCount,
  linkedComponentsCount,
}) => {
  const openEditDrawer = useOpenDrawer(AdditionalComponentEditForm);
  const openModuleCompatibilityDrawer = useOpenDrawer(
    ModuleCompatibilityDrawer
  );
  const component = componentConnection.additional_component;

  const componentName =
    component.additional_component_profiles.find(
      (profile) => profile.language.toLowerCase() === language
    )?.name || component.additional_component_profiles[0]?.name;

  const conditionSummaries = [
    ...summarizeConditions(component.conditions).map((t) => `All: ${t}`),
    ...summarizeConditions(componentConnection.conditions),
  ];

  return (
    <div className="flex items-center py-3 px-4 border-b last:border-b-0 hover:bg-gray-50">
      {/* Image column */}
      <div className="w-[60px] flex-shrink-0">
        {component.image?.src_thumbnail ? (
          <img
            src={component.image.src_md || component.image.src_thumbnail}
            alt={componentName}
            className="w-10 h-10 object-cover rounded-md"
          />
        ) : (
          <div className="w-10 h-10 bg-gray-100 rounded-md flex items-center justify-center">
            <span className="text-gray-400 text-xs">No img</span>
          </div>
        )}
      </div>

      {/* Name column */}
      <div className="flex-1">
        <div className="flex items-center">
          <span className="font-medium">{componentName}</span>
        </div>
        {component.component_sku && (
          <p className="text-xs text-gray-500">
            SKU: {component.component_sku}
          </p>
        )}
      </div>

      {/* Code column */}
      <div className="w-[180px] text-sm text-gray-600">{component.code}</div>

      {/* Status column */}
      <div className="w-[100px]">
        <Badge variant={componentConnection.enabled ? "green" : "secondary"}>
          {componentConnection.enabled ? "Active" : "Inactive"}
        </Badge>
      </div>

      {/* Conditions column */}
      <div className="w-[160px] text-left flex flex-col gap-1">
        {(moduleCompatibilityCount ?? 0) > 0 &&
        componentGroup.code === "armrest" ? (
          <Badge
            variant="lightBlue"
            className="cursor-pointer"
            onClick={() =>
              openModuleCompatibilityDrawer({
                advancedProductId,
                componentCode: component.code,
                componentName,
              })
            }
          >
            Modules:{moduleCompatibilityCount}
          </Badge>
        ) : null}
        {(linkedComponentsCount ?? 0) > 0 ? (
          <Badge variant="secondary">
            Links: {linkedComponentsCount}
          </Badge>
        ) : null}
        {conditionSummaries.map((text) => (
          <span
            key={text}
            className="text-xs text-gray-600 leading-tight"
            title={text}
          >
            {text}
          </span>
        ))}
      </div>
      <div className="w-[100px] text-right">
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 ml-2"
          onClick={() =>
            openEditDrawer({
              id: component.id,
              advancedProductId,
              manufacturerId,
              componentGroup,
            })
          }
        >
          <Edit className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
